// In-app notifications: 12 per organization (4 unread), bilingual, pointing at
// real demo records. Rejections and new pickup requests add more at runtime.

import type { Notification } from '../../types/notification.type';
import { DEMO_OUTLET_ORG_ID, DEMO_RECYCLER_ORG_ID, HOUR_MS, ids, iso } from '../core';
import { materialName } from './catalog';
import { makeCounter, nowIso } from './common';
import { allOrders, type OpsOrder } from './orders';
import { listContainerRecords, siteDisplayName } from './sites';

let notifications: Record<string, Notification[]> = {};
const counter = makeCounter(100);

function at(hoursAgo: number): string {
  return iso(new Date(Date.now() - hoursAgo * HOUR_MS));
}

function note(n: number, title: [string, string], message: [string, string], hoursAgo: number, isRead: boolean, data: Record<string, unknown>): Notification {
  return {
    id: ids.notification(n),
    title_en: title[0],
    title_ar: title[1],
    message_en: message[0],
    message_ar: message[1],
    type: 'in_app',
    data,
    is_read: isRead,
    created_at: at(hoursAgo),
  };
}

function latest(list: OpsOrder[], pred: (o: OpsOrder) => boolean): OpsOrder | undefined {
  return [...list].filter(pred).sort((a, b) => b.updated_at.localeCompare(a.updated_at))[0];
}

function outletNotifications(): Notification[] {
  const orders = allOrders().filter((o) => o.organization_id === DEMO_OUTLET_ORG_ID);
  const inTransit = latest(orders, (o) => o.status === 'in_transit');
  const completed = latest(orders, (o) => o.status === 'completed' && o.approval_status === 'approved');
  const rejected = latest(orders, (o) => o.approval_status === 'rejected');
  const pending = latest(orders, (o) => o.status === 'pending');
  const site = (o?: OpsOrder) => siteDisplayName(o?.site_id) ?? 'Olaya Flagship';
  const fullest = [...listContainerRecords(DEMO_OUTLET_ORG_ID)].sort((a, b) => b.fill_percent - a.fill_percent)[0];
  const binCode = String(fullest?.data?.code ?? 'RUH-OLY-C01');
  const kg = (o?: OpsOrder) => Math.round(o?.verified_kg ?? o?.estimated_kg ?? 0).toLocaleString('en-US');
  return [
    note(1, ['Truck on the way', 'الشاحنة في الطريق'], [`Pickup ${inTransit?.reference ?? ''} at ${site(inTransit)} is in transit to the recycler.`, `طلب الاستلام ${inTransit?.reference ?? ''} من ${site(inTransit)} في الطريق إلى المعيد.`], 0.6, false, { pickup_order_id: inTransit?.id }),
    note(2, ['Container almost full', 'الحاوية شبه ممتلئة'], [`${binCode} (${materialName(fullest?.material_type_id)}) reached ${fullest?.fill_percent ?? 90}% - request a pickup.`, `الحاوية ${binCode} وصلت إلى ${fullest?.fill_percent ?? 90}% - اطلب استلامًا.`], 2.5, false, { container_id: fullest?.id }),
    note(3, ['Pickup request received', 'تم استلام طلب الاستلام'], [`${pending?.reference ?? 'Your request'} for ${materialName(pending?.material_id)} was sent to Riyadh Green Recycling Co.`, `تم إرسال الطلب ${pending?.reference ?? ''} إلى شركة الرياض الخضراء لإعادة التدوير.`], 5, false, { pickup_order_id: pending?.id }),
    note(4, ['Sensor battery low', 'بطارية الحساس منخفضة'], ['Fill sensor RCL-US-02405 at Exit 10 Distribution Centre is at 12% battery.', 'حساس الامتلاء RCL-US-02405 في مركز التوزيع - مخرج 10 بطاريته 12%.'], 9, false, { sensor_id: ids.sensor(5) }),
    note(5, ['Load approved', 'تمت الموافقة على الحمولة'], [`${completed?.reference ?? ''}: ${kg(completed)} kg verified at the plant.`, `${completed?.reference ?? ''}: تم التحقق من ${kg(completed)} كجم في المصنع.`], 20, true, { pickup_order_id: completed?.id }),
    note(6, ['Load rejected', 'تم رفض الحمولة'], [`${rejected?.reference ?? ''} was rejected: ${rejected?.rejection_reason ?? 'contamination'}.`, `تم رفض ${rejected?.reference ?? ''}: ${rejected?.rejection_reason_ar ?? 'تلوث'}.`], 30, true, { pickup_order_id: rejected?.id }),
    note(7, ['Sensor offline', 'الحساس غير متصل'], ['Fill sensor RCL-US-02408 at Al Salamah has not reported for 30 hours.', 'حساس الامتلاء RCL-US-02408 في فرع السلامة لم يرسل بيانات منذ 30 ساعة.'], 34, true, { sensor_id: ids.sensor(8) }),
    note(8, ['Monthly recycling summary', 'ملخص التدوير الشهري'], ['Your September summary is ready: 31.4 t diverted from landfill.', 'ملخص سبتمبر جاهز: تم تحويل 31.4 طن عن المرادم.'], 72, true, {}),
    note(9, ['New collection day', 'يوم جمع جديد'], ['Exit 10 Distribution Centre now has pickups six days a week.', 'مركز التوزيع - مخرج 10 أصبح لديه استلام ستة أيام في الأسبوع.'], 96, true, { site_id: ids.site(4) }),
    note(10, ['Driver arrived', 'وصل السائق'], ['Driver Mohammed Al-Shammari arrived at Al Malqa for pickup.', 'وصل السائق محمد الشمري إلى فرع الملقا للاستلام.'], 120, true, {}),
    note(11, ['Price update', 'تحديث الأسعار'], ['PET bottles now earn SAR 1.40 per kg under your agreement.', 'قوارير PET أصبحت بسعر 1.40 ريال للكيلو حسب الاتفاقية.'], 200, true, {}),
    note(12, ['Welcome to Recyclee Ops', 'مرحبًا بك في Recyclee Ops'], ['Track pickups, containers and sites in one place.', 'تابع الاستلامات والحاويات والمواقع في مكان واحد.'], 400, true, {}),
  ];
}

function recyclerNotifications(): Notification[] {
  const orders = allOrders();
  const fresh = latest(orders, (o) => o.source === 'on_demand' && o.status === 'pending');
  const awaiting = orders.filter((o) => o.status === 'completed' && o.approval_status === 'pending').length;
  return [
    note(13, ['New pickup request', 'طلب استلام جديد'], [`Al Nakheel Markets ${siteDisplayName(fresh?.site_id) ?? ''} requested a ${materialName(fresh?.material_id)} pickup.`, `طلبت منافذ النخيل استلام ${materialName(fresh?.material_id)}.`], 0.4, false, { pickup_order_id: fresh?.id }),
    note(14, ['Loads awaiting verification', 'حمولات بانتظار التحقق'], [`${awaiting} delivered loads are waiting for weight approval.`, `${awaiting} حمولات مسلّمة بانتظار اعتماد الوزن.`], 1.5, false, {}),
    note(15, ['Route started', 'بدأت الجولة'], ['BHT 7752 started Riyadh East Retail Loop (2 of 4 stops done).', 'بدأت الشاحنة BHT 7752 جولة شرق الرياض التجارية.'], 2, false, { truck_id: ids.truck(3) }),
    note(16, ['Licence expiring', 'رخصة على وشك الانتهاء'], ["Fahad Al-Yami's driving licence expires in 22 days.", 'رخصة القيادة للسائق فهد اليامي تنتهي خلال 22 يومًا.'], 6, false, { driver_id: ids.driver(4) }),
    note(17, ['Truck in maintenance', 'الشاحنة في الصيانة'], ['LJA 6640 (Isuzu FVR) is booked in for brake service.', 'الشاحنة LJA 6640 في الصيانة لخدمة المكابح.'], 26, true, { truck_id: ids.truck(5) }),
    note(18, ['Driver declined a request', 'رفض سائق طلبًا'], ['Saad Al-Anazi declined an Al Malqa pickup; it was offered to the next driver.', 'رفض سعد العنزي طلب استلام وتم عرضه على سائق آخر.'], 30, true, {}),
    note(19, ['Run completed', 'اكتملت الجولة'], ['Jeddah Coastal Loop offloaded at Jeddah Transfer Station.', 'تم تفريغ جولة جدة الساحلية في محطة جدة للنقل.'], 40, true, {}),
    note(20, ['Agreement price set', 'تم تحديد سعر الاتفاقية'], ['New OCC price for Granada Mall takes effect next month.', 'سعر الكرتون الجديد لغرناطة مول يسري من الشهر القادم.'], 60, true, { supplier_id: ids.supplier(4) }),
    note(21, ['Collection points synced', 'تمت مزامنة نقاط الجمع'], ['6 sites synced from Al Nakheel Markets Outlet.', 'تمت مزامنة 6 مواقع من منافذ النخيل.'], 90, true, { supplier_id: ids.supplier(1) }),
    note(22, ['Weekly appetite update', 'تحديث الطلب الأسبوعي'], ['OCC demand is 12 t/week; current intake covers 84%.', 'الطلب على الكرتون 12 طن أسبوعيًا؛ التوريد الحالي يغطي 84%.'], 130, true, {}),
    note(23, ['Facility manager assigned', 'تم تعيين مدير المنشأة'], ['Tariq Al-Rashid manages Sulay Sorting Plant.', 'تم تعيين طارق الراشد مديرًا لمصنع فرز السلي.'], 250, true, {}),
    note(24, ['Welcome to Recyclee Ops', 'مرحبًا بك في Recyclee Ops'], ['Plan routes, run your fleet and verify every load.', 'خطط الجولات وأدر أسطولك وتحقق من كل حمولة.'], 420, true, {}),
  ];
}

export function resetNotifications(): void {
  counter.reset();
  notifications = {
    [DEMO_OUTLET_ORG_ID]: outletNotifications(),
    [DEMO_RECYCLER_ORG_ID]: recyclerNotifications(),
  };
}

export function listNotifications(orgId: string): Notification[] {
  return [...(notifications[orgId] ?? [])].sort((a, b) => b.created_at.localeCompare(a.created_at));
}

export function markNotificationRead(orgId: string, id: string): Notification | undefined {
  const n = (notifications[orgId] ?? []).find((x) => x.id === id);
  if (n) n.is_read = true;
  return n;
}

export function addNotification(orgId: string, title: [string, string], message: [string, string], data: Record<string, unknown>): Notification {
  const n: Notification = {
    id: ids.notification(counter.next()),
    title_en: title[0],
    title_ar: title[1],
    message_en: message[0],
    message_ar: message[1],
    type: 'in_app',
    data,
    is_read: false,
    created_at: nowIso(),
  };
  if (!notifications[orgId]) notifications[orgId] = [];
  notifications[orgId].push(n);
  return n;
}
